import { createRouter, createWebHistory } from 'vue-router';
import MapView from '@/views/MapView.vue';
import ListView from '@/views/ListView.vue';
import DeviceManagement from '@/views/Funkcheck.vue';
import BuildingView from '@/views/BuildingView.vue';
import Login from '@/views/Login.vue';
import OrderManagement from '@/views/OrderManagement.vue';
import { useUserStore } from '@/store/user/userStore';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import LoRaWanRadioCheck from '@/views/LoRaWanRadioCheck.vue';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import AppUpdater from '@/views/AppUpdater.vue';
import { useOrderStore } from '@/store/order/orderStore';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models/Notification';
import DevSandbox from '@/views/DevSandbox.vue';
import { WebEvents } from '@/native/plugins/WebEvents';
import OnboardingSandbox from '@/views/OnboardingSandbox.vue';
import DmgwBoardView from '@/views/DmgwBoardView.vue';
import { default as envConfig } from '@/config/runtimeConfig';

export const routesNames = {
  HOME: 'Home',
  BUSINESS_ENTITIES_LIST: 'BusinessEntitiesList',
  FUNKCHECK: 'Funkcheck',
  RADIO_CHECK: 'LoRaWANRadioCheck',
  BUILDING_OVERVIEW: 'Gebäudeübersicht',
  SANDBOX_BUILDING_OVERVIEW: 'SandboxBuildingOverview',
  DETAIL_VIEW: 'Detailansicht',
  LOGIN: 'Login',
  DMGW_BOARD: 'DmgwBoard',
};

const routes = [
  {
    path: '/updater',
    name: 'AppUpdater',
    component: AppUpdater,
    meta: { supportsDarkMode: true },
  },
  {
    path: '/list',
    name: 'BusinessEntitiesList',
    component: ListView,
    meta: { workMode: WorkMode.LIST },
  },
  {
    path: '/',
    name: 'Home',
    component: OrderManagement,
    beforeEnter: () => {
      useEntitiesStore().resetActiveBusinessEntity();
    },
    meta: { workMode: WorkMode.ORDER },
  },
  {
    path: '/detailansicht',
    name: 'Detailansicht',
    component: MapView,
    children: [
      {
        name: 'roomGroup',
        path: 'roomGroup',
        component: { template: '<router-view></router-view>' },
        children: [
          {
            name: 'room',
            path: 'room',
            component: { template: '<router-view></router-view>' },
          },
        ],
      },
    ],
  },
  {
    path: '/funkcheck',
    name: 'Funkcheck',
    component: DeviceManagement,
  },
  {
    path: '/gebaeudeuebersicht',
    name: 'Gebäudeübersicht',
    supportDarkMode: true,
    component: BuildingView,
  },
  {
    path: '/user',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, supportsDarkMode: false },
  },
  {
    path: '/radioCheck',
    name: 'LoRaWANRadioCheck',
    component: LoRaWanRadioCheck,
  },
  {
    path: '/devMode',
    name: 'DevMode',
    component: DevSandbox,
    meta: { supportsDarkMode: true, workMode: WorkMode.DEV_MODE },
  },
  {
    path: '/sandboxMode',
    name: 'sandboxMode',
    component: OnboardingSandbox,
    meta: { supportsDarkMode: true, workMode: WorkMode.SANDBOX },
  },
  {
    path: '/sandboxMode/gebaeudeuebersicht',
    name: 'SandboxBuildingOverview',
    supportDarkMode: true,
    component: BuildingView,
  },
  {
    path: '/dmgw-board',
    name: 'DmgwBoard',
    component: DmgwBoardView,
    // Hiding the side-nav entry is not enough: a stale history entry or a
    // direct URL would still open Q-Tool tooling on an order that has no DMGW6
    // board to pair with. Redirect back rather than render an unusable screen.
    //
    // Development builds are exempt: the board carries the debug trigger for
    // the Q-Tool firmware update, which has to be reachable on any order.
    beforeEnter: () => {
      if (envConfig.isDevelopment === 'true') {
        return true;
      }
      if (!useOrderStore().activeOrder?.hasDmgw6Article()) {
        return { name: routesNames.HOME };
      }
      return true;
    },
    meta: { workMode: WorkMode.ORDER },
  },
  {
    path: '/open/:businessEntityId?/:orderId?',
    name: 'DeepLinkOpen',
    component: { template: '<div />' },
    meta: { workMode: WorkMode.DEEP_LINK },
    beforeEnter: handleDeepLink,
  },
];

const router = createRouter({
  linkActiveClass: 'active',
  history: createWebHistory(),
  scrollBehavior() {
    return { top: 0 };
  },
  routes,
});

router.beforeEach(async (to) => {
  const layoutStore = useLayoutStore();
  layoutStore.setRoute(to.name);
  layoutStore.closeSideMenu();
  if (to.meta.workMode) {
    layoutStore.setWorkMode(to.meta.workMode);
  }
  let isLoggedIn = false;
  let isDispatcher = false;

  try {
    await useUserStore().fetchCurrentUser();
    isLoggedIn = true;
  } catch (error) {
    console.debug('Error fetching user:', error);
    isLoggedIn = false;
  }

  try {
    isDispatcher = await useUserStore().isUserDispatcher();
  } catch (error) {
    throw new Error('Error fetching user group');
  }

  if (to.name === 'Login' && isLoggedIn && !isDispatcher) {
    return { name: 'Home' };
  }

  if (to.name === 'Login' && isLoggedIn && isDispatcher) {
    layoutStore.setWorkMode(WorkMode.LIST);
    return { name: 'BusinessEntitiesList' };
  }

  if (to.name === 'Home' && isLoggedIn && isDispatcher) {
    layoutStore.setWorkMode(WorkMode.LIST);
    return { name: 'BusinessEntitiesList' };
  }

  if (to.meta.requiresAuth !== false && !isLoggedIn) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  if (to.meta.supportsDarkMode === false) {
    layoutStore.disableDarkModeTemporarily();
    return true;
  }
  if (layoutStore.isDarkMode()) {
    layoutStore.activateDarkMode();
    return true;
  }
});

WebEvents.addListener('redirectHome', async () => {
  await router.push({ name: 'Home' });
  WebEvents.reloadWebview();
});

const notifyAndRedirect = (
  message,
  level = 'error',
  redirect = { name: 'Home' }
) => {
  const notificationStore = useNotificationStore();
  notificationStore.addNotification(new Notification(message, '', level));
  return redirect;
};

async function handleDeepLink(to) {
  const entitiesStore = useEntitiesStore();
  const orderStore = useOrderStore();
  const layoutStore = useLayoutStore();

  const businessEntityId = String(to.params.businessEntityId || '');
  const orderId = String(to.params.orderId || '');

  if (!businessEntityId || !orderId) {
    return notifyAndRedirect('Ungültiger Link: fehlende Parameter.');
  }

  try {
    if (!orderStore.getOrderById(orderId)) {
      try {
        await orderStore.fetchOrderById(orderId);
      } catch (error) {
        console.error('Fehler beim Laden des Auftrags:', error);
        return notifyAndRedirect(
          `Auftrag mit ID ${orderId} wurde nicht gefunden.`
        );
      }
    }

    const order = orderStore.getOrderById(orderId);

    if (order.businessEntityId && order.businessEntityId !== businessEntityId) {
      notifyAndRedirect(
        'Hinweis: Die übergebene BusinessEntity-ID stimmt nicht mit dem Auftrag überein.'
      );
    }

    if (order.isEditable) {
      await orderStore.startOrder(orderId);
    } else {
      console.debug(`Order ${orderId} is not editable.`);
    }
    await entitiesStore.setActiveBusinessEntityById(businessEntityId);

    if (entitiesStore.activeBusinessEntity) {
      layoutStore.changeCurrentNode(entitiesStore.activeBusinessEntity);
    }

    return { path: '/gebaeudeuebersicht' };
  } catch (e) {
    console.error(e);
    return notifyAndRedirect(
      'Fehler beim Laden von Auftrag oder BusinessEntity.'
    );
  }
}

export default router;
